import React from 'react';

/**
 * Renders the small subset of inline markdown that shows up in table cells —
 * bold, italic, strikethrough, inline code, and links — as React elements.
 *
 * Deliberately not a full markdown engine and deliberately not
 * `dangerouslySetInnerHTML`: cells hold a single line of source that the grid
 * edits directly, so this only needs to make the common marks legible, and it
 * stays XSS-safe by emitting elements (and validating link hrefs) rather than
 * raw HTML. Unmatched markers fall through as plain text.
 */

const SAFE_HREF = /^(https?:|mailto:)/i;

// Ordered by precedence; the earliest match in the string wins.
const INLINE_PATTERN =
  /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(~~[^~]+~~)|(\*[^*\s][^*]*\*)|(_[^_\s][^_]*_)|(\[[^\]]+\]\([^)]+\))/;

const renderToken = (token: string, key: number): React.ReactNode => {
  if (token.startsWith('`')) {
    return (
      <code
        key={key}
        className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]"
      >
        {token.slice(1, -1)}
      </code>
    );
  }
  if (token.startsWith('**') || token.startsWith('__')) {
    return <strong key={key}>{token.slice(2, -2)}</strong>;
  }
  if (token.startsWith('~~')) {
    return <del key={key}>{token.slice(2, -2)}</del>;
  }
  if (token.startsWith('*') || token.startsWith('_')) {
    return <em key={key}>{token.slice(1, -1)}</em>;
  }
  const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
  if (link !== null) {
    const [, label, href] = link;
    if (href !== undefined && SAFE_HREF.test(href)) {
      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline"
        >
          {label}
        </a>
      );
    }
    return <React.Fragment key={key}>{label}</React.Fragment>;
  }
  return <React.Fragment key={key}>{token}</React.Fragment>;
};

const parseInline = (input: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let remaining = input;
  let key = 0;

  while (remaining.length > 0) {
    const match = INLINE_PATTERN.exec(remaining);
    if (match === null || match.index === undefined) {
      nodes.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
      break;
    }
    if (match.index > 0) {
      nodes.push(
        <React.Fragment key={key++}>
          {remaining.slice(0, match.index)}
        </React.Fragment>
      );
    }
    nodes.push(renderToken(match[0], key++));
    remaining = remaining.slice(match.index + match[0].length);
  }

  return nodes;
};

export const InlineMarkdown: React.FC<{ text: string }> = ({ text }) => (
  <>{parseInline(text)}</>
);
